/**
 * AgentPipelineVisualisation — live call flow graph for the research pipeline.
 *
 * Renders a React Flow graph with 4 nodes (Graph Engine + 3 agents) showing
 * real-time state transitions driven by WebSocket progress_update events.
 *
 * Sits above the log stream on ProcessPage. Non-interactive (display only).
 */

import React from 'react';
import ReactFlow, { Handle, Position, BaseEdge, getStraightPath } from 'reactflow';
import useAgentPipelineState from '../hooks/useAgentPipelineState';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const NODE_STYLES = {
  pending: {
    border: '1px dashed #8d99a8',
    background: '#1a2332',
    color: '#8d99a8',
  },
  active: {
    border: '2px solid #0972d3',
    background: '#0d1f35',
    color: '#ffffff',
  },
  complete: {
    border: '2px solid #1d8348',
    background: '#0d2818',
    color: '#ffffff',
  },
  error: {
    border: '2px solid #c0392b',
    background: '#2d0d0d',
    color: '#ffffff',
  },
};

const EDGE_STYLES = {
  pending: { stroke: '#8d99a8', strokeDasharray: '5 5', strokeWidth: 1.5 },
  active: { stroke: '#0972d3', strokeWidth: 2 },
  complete: { stroke: '#1d8348', strokeWidth: 2 },
};

// ---------------------------------------------------------------------------
// CSS Animations (injected once)
// ---------------------------------------------------------------------------

const ANIMATION_CSS = `
@keyframes nodePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(9, 114, 211, 0.4); }
  50%       { box-shadow: 0 0 0 6px rgba(9, 114, 211, 0); }
}
@keyframes flowDash {
  from { stroke-dashoffset: 20; }
  to   { stroke-dashoffset: 0; }
}
.agent-node-active {
  animation: nodePulse 2s infinite;
}
.edge-active path {
  stroke-dasharray: 10 5;
  animation: flowDash 1s linear infinite;
}
.react-flow__renderer { width: 100%; height: 100%; }
.react-flow__viewport { transform-origin: 0 0; }
.react-flow__node { position: absolute; }
.react-flow__edge { pointer-events: visibleStroke; }
.react-flow__edge path { fill: none; }
`;

// ---------------------------------------------------------------------------
// Custom Node
// ---------------------------------------------------------------------------

function AgentNode({ data }) {
  const { label, icon, status, completedCount, totalCount, currentActivity, elapsedTime } = data;
  const style = NODE_STYLES[status] || NODE_STYLES.pending;

  const subLabel = status === 'complete'
    ? `✓ ${completedCount} / ${totalCount} complete${elapsedTime ? ` • ${elapsedTime.toFixed(1)}s` : ''}`
    : status === 'active'
      ? currentActivity || `${completedCount} / ${totalCount}`
      : 'Waiting...';

  return (
    <div
      className={status === 'active' ? 'agent-node-active' : ''}
      style={{
        ...style,
        borderRadius: 8,
        padding: '8px 12px',
        width: 150,
        minHeight: 64,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        fontSize: 12,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      aria-label={`${label} — ${status}, ${completedCount} of ${totalCount} questions complete`}
    >
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
        {icon} {label}
      </div>
      <div style={{
        fontSize: 11,
        opacity: 0.85,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {subLabel}
      </div>
      <Handle type="source" position={Position.Right} style={{ visibility: 'hidden' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom Edge
// ---------------------------------------------------------------------------

function AnimatedEdge({ id, sourceX, sourceY, targetX, targetY, data }) {
  const [edgePath] = getStraightPath({ sourceX, sourceY, targetX, targetY });
  const status = data?.status || 'pending';
  const style = EDGE_STYLES[status] || EDGE_STYLES.pending;

  return (
    <g className={status === 'active' ? 'edge-active' : ''}>
      <BaseEdge id={id} path={edgePath} style={style} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Node/Edge type registrations (must be stable references)
// ---------------------------------------------------------------------------

const nodeTypes = { agentNode: AgentNode };
const edgeTypes = { animatedEdge: AnimatedEdge };

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * AgentPipelineVisualisation
 *
 * Two usage modes:
 *
 * 1. Research pipeline (default) — pass isProcessing, processingComplete, progressData.
 *    State is derived from WebSocket progress_update events via useAgentPipelineState.
 *
 * 2. Custom pipeline — pass staticNodes and staticEdges directly.
 *    Used by exec briefing and other pages with their own state.
 */
export default function AgentPipelineVisualisation({
  // Research pipeline mode
  isProcessing, processingComplete, progressData,
  // Custom pipeline mode
  staticNodes, staticEdges,
  ariaLabel,
}) {
  const derived = useAgentPipelineState(progressData, processingComplete, isProcessing);

  const nodes = staticNodes || derived.nodes;
  const edges = staticEdges || derived.edges;
  const announcement = derived.announcement;

  return (
    <div
      role="img"
      aria-label={ariaLabel || "Research pipeline progress"}
      style={{ height: 220, width: '100%', position: 'relative', background: 'transparent' }}
    >
      <style>{ANIMATION_CSS}</style>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnDrag={false}
        panOnScroll={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
        style={{ background: 'transparent' }}
        defaultViewport={{ x: 10, y: 30, zoom: 0.95 }}
      />

      <div aria-live="polite" aria-atomic="true" style={{
        position: 'absolute', width: 1, height: 1, overflow: 'hidden',
        clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap'
      }}>
        {announcement}
      </div>
    </div>
  );
}
